import { memo, useState, ChangeEvent, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { useDebounce } from '@hooks/useDebounce';
import { useIsComponentMounted } from '@hooks/useIsComponentMounted';
import { translate } from '@utils';
import { contactTypesOptions } from '@constants/submissionsTypes';
import { currentSubmissionTemplateEditorFormChanged } from '@selectors/submission';
import { TextFormField } from '@components/SharedComponents/Form/TextFormField';
import { SelectFormField } from '@components/SharedComponents/Form/SelectFormField';
import { CheckboxFormField } from '@components/SharedComponents/Form/CheckboxFormField';

import { OriginBlockFormValueKey } from '@enums';

import './index.scss';

import RichTextEditorFormField from '@sharedComponents/RichTextEditor';

const fieldLabelClasses = {
  root: 'field-label',
};

export interface FormValues {
  [OriginBlockFormValueKey.Title]?: string;
  [OriginBlockFormValueKey.Link]?: string;
  [OriginBlockFormValueKey.Description]?: string;
  [OriginBlockFormValueKey.ContactsType]?: string;
  [OriginBlockFormValueKey.HasBottomBorder]?: boolean;
}

interface OriginSectionEditorFormProps {
  title?: string;
  link?: string;
  description?: string;
  contactsType?: string;
  hasBottomBorder?: boolean;
  onFormChange?: (updatedFormValues: FormValues, updatedValueKey: OriginBlockFormValueKey) => void;
}

export const OriginSectionEditorForm = memo(
  ({
    title,
    link,
    description,
    contactsType,
    hasBottomBorder,
    onFormChange,
  }: OriginSectionEditorFormProps) => {
    const formValueFromState = { title, link, description, contactsType, hasBottomBorder };
    const [formValues, setFormValues] = useState<FormValues>(formValueFromState);
    const [lastUpdatedValueKey, setLastUpdatedValueKey] = useState<OriginBlockFormValueKey>(null);
    const isComponentMounted = useIsComponentMounted();
    const isTemplateEditorFormChanged = useSelector(currentSubmissionTemplateEditorFormChanged);

    useEffect(() => {
      if (!isTemplateEditorFormChanged && !isEqual(formValueFromState, formValues)) {
        setFormValues(formValueFromState);
      }
    }, [title, link, description, contactsType, hasBottomBorder]);

    // Debounced form values
    const debouncedFormValues = useDebounce<FormValues>(formValues, 200);

    useEffect(() => {
      // We use isComponentMounted to skip executing useEffect for the first component render
      onFormChange && isComponentMounted && onFormChange(debouncedFormValues, lastUpdatedValueKey);
    }, [debouncedFormValues, onFormChange]);

    // Handlers
    const onTextFieldChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setLastUpdatedValueKey(name as OriginBlockFormValueKey);
        setFormValues((prevValue) => ({ ...prevValue, [name]: value }));
      },
      [setFormValues]
    );

    const onRichTextEditorChange = useCallback(
      (value: string) => {
        setLastUpdatedValueKey(OriginBlockFormValueKey.Description);
        setFormValues((prevValue) => ({
          ...prevValue,
          [OriginBlockFormValueKey.Description]: value,
        }));
      },
      [setFormValues]
    );

    const onContactsTypeFieldChange = useCallback(
      (value: string) => {
        setLastUpdatedValueKey(OriginBlockFormValueKey.ContactsType);
        setFormValues((prevValue) => ({ ...prevValue, contactsType: value }));
      },
      [setFormValues]
    );

    const onBorderFieldChange = useCallback(
      (_, value: boolean) => {
        setLastUpdatedValueKey(OriginBlockFormValueKey.HasBottomBorder);
        setFormValues((prevValue) => ({ ...prevValue, hasBottomBorder: value }));
      },
      [setFormValues]
    );

    return (
      <div className="form-container">
        <TextFormField
          dataTestId="section-block-title"
          name={OriginBlockFormValueKey.Title}
          value={formValues.title}
          onChange={onTextFieldChange}
          label={translate('title')}
          fieldLabelClasses={fieldLabelClasses}
        />

        <TextFormField
          dataTestId="section-block-link"
          name={OriginBlockFormValueKey.Link}
          value={formValues.link}
          onChange={onTextFieldChange}
          label={translate('link')}
          fieldLabelClasses={fieldLabelClasses}
        />

        <RichTextEditorFormField
          label={translate('description')}
          value={formValues.description}
          onChange={onRichTextEditorChange}
          labelClasses={fieldLabelClasses}
          className={'input'}
          testId={'section-block-description'}
        />

        <SelectFormField
          dataTestId="section-block-contacts-type"
          label={translate('contacts_type')}
          value={formValues.contactsType}
          options={contactTypesOptions}
          onChange={onContactsTypeFieldChange}
          fieldLabelClasses={fieldLabelClasses}
        />

        <CheckboxFormField
          dataTestId="section-block-has-bottom-border"
          value={formValues.hasBottomBorder}
          onChange={onBorderFieldChange}
          label={translate('border')}
        />
      </div>
    );
  }
);

OriginSectionEditorForm.displayName = 'OriginSectionEditorForm';
